import { useCallback, useEffect, useState } from "react";
import { fetchZones } from "../lib/OptionListApi";
import { ZoneRaw } from "../types/sortingMachine";

/**
 * Module-level cache of `GET /api/Zone/zones`, for the same reason `useCurrencies` caches its list.
 *
 * The zone hierarchy is reference data that does not change within a session, and a dozen pages
 * already fetch the whole thing and filter it client-side. `useSortingMachine` refetches on every
 * mount precisely because no shared cache existed.
 *
 * There is deliberately no invalidation; a page reload refetches.
 */
let zonesPromise: Promise<ZoneRaw[]> | null = null;
let zonesCache: ZoneRaw[] | null = null;

const loadZones = () => {
	if (!zonesPromise) {
		zonesPromise = fetchZones().then((result: unknown) => {
			// `fetchZones` resolves to undefined on failure. Falling back to an empty list rather
			// than throwing means a zone selector built on this renders with nothing but its
			// "whole country" entry — which is exactly the pre-zone behaviour, the right way to
			// degrade.
			const zones = Array.isArray(result) ? (result as ZoneRaw[]) : [];
			zonesCache = zones;
			return zones;
		});
	}
	return zonesPromise;
};

/** `Zones.LevelId`: the hierarchy is exactly three levels deep. */
export const ZONE_LEVEL_COUNTRY = 1;
export const ZONE_LEVEL_CITY = 2;
export const ZONE_LEVEL_SECTION = 3;

export interface TariffZoneOption {
	value: number;
	label: string;
	/** False for a city with no sections — no order's zone chain can pass through it. */
	reachable: boolean;
}

/**
 * The zone hierarchy, loaded once per session.
 *
 * `tariffZonesOf` is the shape callers actually want: every zone under one country that a tariff
 * can be keyed to — the country, its cities and their sections — labelled and ordered for a
 * `DefaultSelect`.
 */
const useZones = () => {
	// Seeded from the cache so a hook mounting after the first fetch resolved never renders an
	// empty selector for a frame.
	const [zones, setZones] = useState<ZoneRaw[]>(zonesCache ?? []);
	const [loading, setLoading] = useState(zonesCache === null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const result = await loadZones();
			if (!cancelled) {
				setZones(result);
				setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	/**
	 * Every zone under one country that a tariff can be keyed to: its cities (level 2) and their
	 * sections (level 3). Ordered city-then-its-own-sections rather than alphabetically overall, so
	 * a section always reads directly under the city it belongs to.
	 *
	 * Sections are qualified with their city — a bare "TR-1" or "DB-1" means nothing on its own,
	 * and the same section code is reused across cities.
	 *
	 * Each option carries `reachable`. Orders address a level-3 section — every one of the 1.14M
	 * rows on PostmanAL does — and pricing walks up from there, so a city with no sections beneath
	 * it can never be on any order's chain. A tariff saved against one applies to nothing and says
	 * nothing about it. Albania has four: Diber (308), Kamez (91), Kerrabe (73), Mat (96) — two of
	 * which are the orphaned halves of duplicate city names. They stay selectable, because sections
	 * can be added later and the price would then take effect; the caller is expected to say so.
	 */
	const tariffZonesOf = useCallback(
		(countryId?: number) => {
			if (!countryId) return [];

			// Several descriptions carry a trailing tab in the data ("Periferi Tirane\t",
			// "Fushe-Arrez\t", ...), which breaks both the sort and the rendered label.
			const nameOf = (zone: ZoneRaw) => (zone.description ?? "").trim();
			const byName = (a: ZoneRaw, b: ZoneRaw) =>
				nameOf(a).localeCompare(nameOf(b));

			const sectionsByCity = new Map<number, ZoneRaw[]>();
			zones
				.filter((zone) => zone.levelId === ZONE_LEVEL_SECTION)
				.forEach((section) => {
					const siblings = sectionsByCity.get(section.parentId);
					if (siblings) {
						siblings.push(section);
					} else {
						sectionsByCity.set(section.parentId, [section]);
					}
				});

			const options: TariffZoneOption[] = [];

			// The country itself is a valid tariff zone — it is where every default template lives
			// today. Offering it explicitly means "the national price list" is a thing the operator
			// selects and sees, rather than an absence they have to infer from an empty selector.
			const country = zones.find((zone) => zone.id === countryId);
			if (country) {
				options.push({
					value: country.id,
					label: nameOf(country),
					reachable: true,
				});
			}

			zones
				.filter(
					(zone) =>
						zone.levelId === ZONE_LEVEL_CITY &&
						zone.parentId === countryId
				)
				.sort(byName)
				.forEach((city) => {
					const cityName = nameOf(city);
					const sections = sectionsByCity.get(city.id) ?? [];
					options.push({
						value: city.id,
						label: cityName,
						reachable: sections.length > 0,
					});
					sections.sort(byName).forEach((section) =>
						options.push({
							value: section.id,
							label: `${cityName} › ${nameOf(section)}`,
							// A section is what orders actually carry, so it is always on a chain.
							reachable: true,
						})
					);
				});

			// Names repeat as genuinely separate Zones rows — Albania has two "Kamez" (70, 91), two
			// "Vore" (79, 130) and two "Diber" (119, 308), and section codes repeat within a city
			// too ("SU-1" is both 319 and 339 under Struge). Bare names would leave the operator
			// choosing blind, and a tariff saved against the wrong row is unreachable by every
			// order without ever reporting an error. Only the ambiguous labels are qualified with
			// an id, so everything else stays clean.
			const occurrences = new Map<string, number>();
			options.forEach((option) =>
				occurrences.set(
					option.label,
					(occurrences.get(option.label) ?? 0) + 1
				)
			);

			return options.map((option) =>
				(occurrences.get(option.label) ?? 0) > 1
					? { ...option, label: `${option.label} (${option.value})` }
					: option
			);
		},
		[zones]
	);

	return { loading, zones, tariffZonesOf };
};

export default useZones;
